import { useQuery } from "@apollo/client";
import { Navigate, useParams } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import usePageTitle from "@/hooks/usePageTitle";
import { SectionOfficeContactDetails } from "./_components/SectionOfficeContactDetails";
import { getSectionOperationsFallbackPath } from "../_utils";
import { SECTION_OFFICE_DETAILS_QUERY } from "./queries";
import { useSectionOperationsAccess } from "./useSectionOperationsAccess";

export default function SectionOperationsContactPage() {
  const { sectionId } = useParams();
  const { ready, canViewFacilities, showCommittees, showGovernance, showNotifications } =
    useSectionOperationsAccess(sectionId);
  const { data, loading, error } = useQuery(SECTION_OFFICE_DETAILS_QUERY, {
    variables: { sectionId: sectionId ?? "" },
    skip: !sectionId || !canViewFacilities,
    fetchPolicy: "cache-first"
  });

  usePageTitle(`/sections/${sectionId}/operations/contact`, "Sections | Operations | Contact");

  if (!sectionId || !ready) {
    return null;
  }

  if (!canViewFacilities) {
    return (
      <Navigate
        to={getSectionOperationsFallbackPath(sectionId, {
          showCommittees,
          showGovernance,
          showNotifications
        })}
        replace
      />
    );
  }

  if (loading && !data) {
    return null;
  }

  // Contact uses SECTION_OFFICE_DETAILS_QUERY for facility data; ignore SECTION_NAV failures
  // when details already returned an office (Employees still needs the nav id).
  if (error && !data?.section?.sectionOffice) {
    return (
      <div className="w-full" data-testid="section-operations-contact">
        <ErrorBoundary
          variant="inline"
          title="Failed to Load Contact Details"
          message="We couldn't load contact details for this section's office facility. Please try again or contact tech support."
          className="mt-12"
        />
      </div>
    );
  }

  const facility = data?.section?.sectionOffice;
  if (!facility) {
    return (
      <div className="w-full" data-testid="section-operations-contact">
        <EmptyState
          iconName="store"
          title="No Section Office Facility"
          caption="This section does not have a section office facility assigned."
        />
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="section-operations-contact">
      <SectionOfficeContactDetails
        facility={facility}
        operatingStatusOptions={data.operatingStatusOptions}
        facilityTypeOptions={data.facilityTypeOptions}
      />
    </div>
  );
}
